/**
 * Email domains that may never be used to sign up or to connect a mailbox.
 *
 * Source: the Spamhaus ASN-DROP list (438 records, 274 unique domains). Each
 * record names an autonomous system Spamhaus considers controlled by, or wholly
 * leased to, actors it will not route mail for; the `domain` field is the
 * operator that registered the ASN.
 *
 * **Read that before adding to it.** These are the AS OPERATOR's domains, not
 * observed spam-sending domains. Most are bulletproof hosts and proxy vendors,
 * but the list also holds ordinary businesses that merely own a listed ASN, so
 * blocking on it rejects some legitimate addresses. That trade was made
 * deliberately; the remedy for a false positive is to delete the line, which is
 * why this is a literal list rather than a fetched feed.
 *
 * **Static on purpose.** Fetching this at runtime would add a failure mode to
 * account creation, and "the blocklist source is down" has only two answers,
 * both bad: fail open (the list stops existing) or fail closed (nobody can sign
 * up). A literal list has neither, and it is reviewable in a diff. Refresh it by
 * regenerating from a newer ASN-DROP dump.
 *
 * Lowercase and sorted. Entries are bare hosts — no `@`, scheme or path;
 * {@link isBlockedEmailDomain} compares against the host alone.
 */
import disposableEmailDomains from './disposable-email-domains.json';

export const BLOCKED_EMAIL_DOMAINS: readonly string[] = [
  '493networking.cc',
  '62yun.com',
  '69host.cc',
  'ace-idc.com',
  'acerdp.io',
  'adoard.com',
  'aeza.net',
  'agrofirma-aleks',
  'ahamed.com.bd',
  'aleducacional.com.br',
  'algoz.co.in',
  'almaseabi.net',
  'alphainfolab.com',
  'altawk.com',
  'ananyacomp.in',
  'appserv.co.nz',
  'arkcomtele.com',
  'as210558.net',
  'as214351.com',
  'atom3.com.ua',
  'auproxies.com',
  'avtotrans',
  'bakrietelecom.com',
  'barebox.com.bd',
  'berch.co.uk',
  'berdiev-ruslan-mukhabatovich',
  'bhagwatsoft.in',
  'bignet.ua',
  'biil.ru',
  'blackappleus.com',
  'blueberrywebs.com',
  'bluecentrix.co.za',
  'bluegate-exchange.co.za',
  'brutalproxies.com',
  'bst.lt',
  'btcloud.ro',
  'bubblesalter',
  'bunea.eu',
  'bunnycommunications.com',
  'caiwireless.net',
  'cantech.international',
  'changway.hk',
  'cheapy.host',
  'chempir',
  'chosting.solutions',
  'cloudbs.biz',
  'cloudie.hk',
  'club-nadiya',
  'cognetcloud.com',
  'colocatel.com',
  'contell.ru',
  'ctgserver.com',
  'cubilloconstrucciones.com',
  'cyberauticssoftwares.co.in',
  'cybrscrb.com',
  'd-conect',
  'dadeh-pardazesh-boroumand-kerman',
  'darkclub.com.ua',
  'dataparadise.net',
  'datawingstel.in',
  'davidascottmotorsltd',
  'ddps.jp',
  'dedik.io',
  'digivps.com',
  'dm-auto.eu',
  'dmzhost.co',
  'dobrosvitkredo',
  'dolphinhost.net',
  'donserver.cl',
  'eggywall.org',
  'eikontech.net',
  'ekoiniciative.pp.ua',
  'eksenbilisim.com.tr',
  'el-k-stil',
  'elekoms.net.ua',
  'elende.ao',
  'eliteteam.to',
  'empire-communications',
  'enterprises-holding',
  'erishennya',
  'ethr.net',
  'evokedigital.in',
  'excitedmatch.com',
  'farahoosh.ir',
  'fastnetcpl.com',
  'feoprest.info',
  'fibacloud.com',
  'fineproxy.org',
  'flowerproxy.com',
  'fly-group.ru',
  'frostyhosting',
  'fulltimehosting.net',
  'g-w.bz',
  'garant-plus-inform',
  'ghostynetworks.com',
  'girdharicomp.in',
  'globalsol.co.in',
  'globaltelehost.com',
  'globtelgroup.com',
  'gmtech.com.bd',
  'grandwebsolutions.com',
  'gwhostinglabs.com',
  'h2.nexus',
  'heebshealthcare.co.in',
  'herbalvedawellness.com',
  'herbzoothealthcare.co.in',
  'hosterdaddy.com',
  'hostglobal.plus',
  'hostslick.de',
  'hts.vn',
  'hypernex.cc',
  'idodns.com',
  'iic-rail',
  'imaginetechnosoft.co.in',
  'imasfs.com',
  'impactsoftwares.co.in',
  'imtigernet',
  'infiniroute.io',
  'intechmandiri.com',
  'interspireaddon.co.in',
  'intintell.co.uk',
  'invisionchiptechnosoft.co.in',
  'ipconnect.services',
  'iprowireless.com',
  'ipswat.com',
  'ipv4holdings.net',
  'ipvolume.net',
  'isofttechnology.net',
  'ithostline.com',
  'itsidc.com',
  'jdmbroadband.com',
  'jellydigital.net',
  'jetcloud.vn',
  'kaopuyun.com',
  'khalidgroup.co',
  'king-servers.com',
  'klikindonesia.or.id',
  'kontrast.md',
  'kyliecdn',
  'kyonix.com',
  'lhwebtech.com',
  'link-host.com',
  'liptel.net.ua',
  'livecomm',
  'livela',
  'lordvps.net',
  'mabasafenet.com',
  'madgenius.com',
  'management-alliance-group',
  'medianetdigital.co',
  'morsejp.com',
  'mortalsoft.online',
  'mxfiber.com',
  'myce.net.cn',
  'mylir.co.uk',
  'neoncore.biz',
  'net-gate.ro',
  'net4india.com',
  'netiface.co.uk',
  'netinnovation.net',
  'netip.com.ua',
  'networldtron.net',
  'nexon.co.kr',
  'niutelecom.com',
  'nktele.com',
  'nodonorte.net.ar',
  'noobtech.in',
  'noorhost.com.bd',
  'notbad.cloud',
  'novuslabslimited.com',
  'novx-systems',
  'ntel.com.ng',
  'nts.center',
  'ntup.net',
  'nuxt.cloud',
  'nybula.com',
  'obhost.org',
  'onlinepragathi.com',
  'orbittelekom.com',
  'ozon.ru',
  'pfcloud.io',
  'pilot.net',
  'pitline.net',
  'pixoof.com',
  'plainproxies.com',
  'planningresearchcorp.com',
  'pro-spero.ru',
  'proton66.ru',
  'prv8net',
  'psb.hosting',
  'pwnhost.com',
  'qlhost.cc',
  'qtcloud.co.jp',
  'quarterhill.com',
  'qwins.co',
  'rapidoserver.com',
  'rasuhost.com.bd',
  'reapolis',
  'regxa.iq',
  'rinnai.co.kr',
  'rmhospitality.co.in',
  'robustedge.com',
  'rocketmediagroups.com',
  'rootlayer.net',
  'rosa-holidays.com',
  'ryzehosting.com',
  's-host.com.ua',
  'sajibhost.com.bd',
  'scloud.sg',
  'seacom.cc',
  'securecommsgroup.xyz',
  'serv.host',
  'server-panel.org',
  'serverion.com',
  'serveroffer.lt',
  'servicehk.net',
  'seyix.sc',
  'sfns',
  'shereverov.marat',
  'sircrosar.net',
  'skntelecom.com',
  'slayergroup.ltd',
  'smartmieten.in',
  'smtpmailers.com',
  'snowcore.io',
  'sovy.cloud',
  'spectrenetworks.net',
  'spnhost.com.bd',
  'srjnhospitality.com',
  'sshvps.net',
  'ssnet.eu',
  'stark-industries.solutions',
  'start-building',
  'stat-cons',
  'stealthvm.net',
  'stormindustries.llc',
  'sufia.tv',
  'sulavi.co.uk',
  'sulharai.com',
  'sunucun.com.tr',
  'swissnetwork.io',
  'tavriatrans.com.ua',
  'telepatiya.kz',
  'telerakesh.in',
  'thepinnaclegroup.in',
  'thestack.net',
  'thoimmo.com',
  'torero',
  'trit.net',
  'tube-hosting.de',
  'turingserver.com',
  'turkbil.com.tr',
  'vaultdweller.net',
  'vedimantra.co.in',
  'vinahost.vn',
  'vip-net.net',
  'virtualine.org',
  'volumedrive.com',
  'vonie.net',
  'vpsdedic.ru',
  'vpsdedicated.net',
  'vpsvault.host',
  'webwiresolutions.com',
  'whitelabel.sh',
  'wirelesscuracao.com',
  'xdeer.com',
  'xor.sc',
  'xproxies.com',
  'zaifcomp.in',
  'zen4softsolution',
  'zexotek.de',
  'zhongguancun.asia',
  'zillionnetwork.com',
  'zumy.eu',
] as const;

/**
 * O(1) membership set, built once at module load.
 *
 * The array stays exported because it is the reviewable artifact — what a person
 * reads in a diff — while every lookup goes through this.
 */
const BLOCKED_EMAIL_DOMAIN_SET: ReadonlySet<string> = new Set(BLOCKED_EMAIL_DOMAINS);

/**
 * Disposable / temp-mail domains (mailinator.com, 10minutemail.com, …).
 *
 * A second, far larger list than {@link BLOCKED_EMAIL_DOMAINS} and a different
 * category: those are AS operators Spamhaus will not route for, these are
 * throwaway-inbox providers. Kept as a separate JSON so the ASN list above stays
 * a reviewable literal with its own provenance, and so this one can be
 * regenerated wholesale without touching it.
 *
 * The same file is shipped in trulyinbox-edge and trulyinbox-warmup-manager at
 * `src/common/static/blacklisted-domains.json`; all three must be updated
 * together.
 */
export const DISPOSABLE_EMAIL_DOMAIN_SET: ReadonlySet<string> = new Set(
  Object.keys(disposableEmailDomains),
);

/**
 * Dot-anchored suffix match against `set`: `mail.fineproxy.org` matches an entry
 * of `fineproxy.org`, `notfineproxy.org` does not.
 *
 * Walks the host's own suffixes (`a.b.c` -> `a.b.c`, `b.c`, `c`) rather than
 * iterating the set. Same answer as a scan, but the cost is the number of labels
 * in the host — three or four — instead of the size of the list. With ~100k
 * disposable domains a per-entry scan would run 100k `endsWith` calls on every
 * signup and mailbox connect.
 */
function matchesDomainOrSubdomain(host: string, set: ReadonlySet<string>): boolean {
  if (set.has(host)) return true;
  for (let i = host.indexOf('.'); i !== -1; i = host.indexOf('.', i + 1)) {
    if (set.has(host.slice(i + 1))) return true;
  }
  return false;
}

/**
 * The lowercased host of an email address, or null when the input is not shaped
 * like one.
 *
 * Splits on the LAST `@`: a quoted local part may legally contain one
 * (`"a@b"@example.com`), and splitting on the first would yield a host of
 * `b"@example.com`, which matches nothing and silently lets the address through.
 *
 * Trailing dots are trimmed because `user@example.com.` names the same host as
 * `user@example.com` to every resolver — leaving them would be a one-character
 * bypass of the entire list.
 */
export function extractEmailDomain(value: string | null | undefined): string | null {
  if (!value) return null;
  const at = value.lastIndexOf('@');
  if (at < 1 || at === value.length - 1) return null;
  const host = value.slice(at + 1).trim().toLowerCase().replace(/\.+$/, '');
  return host === '' ? null : host;
}

/**
 * Whether `value` — a full email address, or a bare domain — is blocked.
 *
 * Matches the domain AND any subdomain of it: `fineproxy.org` blocks
 * `user@fineproxy.org` and `user@mail.fineproxy.org` alike. Without the suffix
 * rule the list is bypassed by pointing a subdomain at the same mailboxes, which
 * costs the operator one DNS record.
 *
 * The suffix test is anchored on a dot, so `notfineproxy.org` does NOT match
 * `fineproxy.org` — a bare `endsWith` would block unrelated registrable domains
 * that happen to share a tail.
 *
 * Accepts a bare domain as well as an address so a caller holding only a host
 * needs no second entry point. Returns false for anything unparseable: this
 * answers "is it blocked", never "is it valid". Address validation belongs to
 * the DTO, and conflating the two would report a malformed address as a blocked
 * domain.
 *
 * `whitelistedEmails` is the manual allowlist (`whitelistedEmailAddresses`),
 * matched on the exact address ({@link isEmailInSet}): a listed address is
 * never blocked, and nothing else on its domain is unblocked with it. A bare
 * domain never matches it. Pure — the caller loads the set once (one query per
 * request or batch) so a bulk path can test every row without a per-row await.
 */
export function isBlockedEmailDomain(
  value: string | null | undefined,
  whitelistedEmails?: ReadonlySet<string>,
): boolean {
  const host = toHost(value);
  if (!host) return false;
  if (whitelistedEmails && isEmailInSet(value, whitelistedEmails)) return false;

  return (
    matchesDomainOrSubdomain(host, BLOCKED_EMAIL_DOMAIN_SET) ||
    matchesDomainOrSubdomain(host, DISPOSABLE_EMAIL_DOMAIN_SET)
  );
}

/**
 * The comparison key for an allowlisted address: trimmed, lowercased, trailing
 * dot dropped from the host — the shape `whitelistedEmailAddresses.email` is
 * stored in. Null for anything that is not `local@host`. No provider-specific
 * folding (Gmail dots, `+tag`): an allowlist row names one exact mailbox.
 */
export function normalizeWhitelistEmail(value: string | null | undefined): string | null {
  const host = extractEmailDomain(value);
  if (!host || !value) return null;
  const local = value.slice(0, value.lastIndexOf('@')).trim().toLowerCase();
  return local === '' ? null : `${local}@${host}`;
}

/** Whether the address `value` is on `emails` (keys from {@link normalizeWhitelistEmail}). False for anything unparseable. */
export function isEmailInSet(
  value: string | null | undefined,
  emails: ReadonlySet<string>,
): boolean {
  const key = normalizeWhitelistEmail(value);
  return key !== null && emails.has(key);
}

/** The lowercased host of an address, or of a bare domain; null when unparseable. */
function toHost(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.includes('@')
    ? extractEmailDomain(value)
    : value.trim().toLowerCase().replace(/\.+$/, '') || null;
}

/**
 * Domains (saleshandy.com, trulyinbox.com) whose staff may sign up with a `+tag` alias (internal QA / support
 * testing); everyone else is refused by `isPlusAliasEmail`. Matches the domain
 * only, NOT subdomains — `x@evil.saleshandy.com.attacker.io` and
 * `x@mail.saleshandy.com` stay blocked.
 */
export const PLUS_ALIAS_ALLOWED_DOMAINS: ReadonlySet<string> = new Set(['saleshandy.com', 'trulyinbox.com']);

/**
 * Whether `value` is a `+tag` sub-address (`jane+promo@example.com`) on a domain
 * that does not allow them. Splits on the LAST `@` (see `extractEmailDomain`) so
 * a quoted local part containing `@` can't hide the `+`.
 */
export function isPlusAliasEmail(value: string | null | undefined): boolean {
  const host = extractEmailDomain(value);
  if (!host || !value) return false;
  if (PLUS_ALIAS_ALLOWED_DOMAINS.has(host)) return false;
  return value.slice(0, value.lastIndexOf('@')).includes('+');
}
